import { PropsWithChildren, useEffect, useRef, useState } from 'react'

import { useDebounceFn, useMount } from 'ahooks'
import { createStyles, keyframes } from 'antd-style'

import { AutoScrollProps } from './interface'

const verticalScrollAnimation = keyframes('vertical-infinite-scroll', {
  '0%': {
    transform: 'translateY(0)'
  },
  '100%': {
    transform: ' translateY(-50%)'
  }
})

const horizontalScrollAnimation = keyframes('horizontal-infinite-scroll', {
  '0%': {
    transform: 'translateX(0)'
  },
  '100%': {
    transform: ' translateX(-50%)'
  }
})

const useStyles = createStyles(({ token }) => ({
  rootWrapper: {
    width: '100%',
    height: '100%',
    overflow: 'hidden'
  },
  scroller: {
    display: 'flex',
    flexWrap: 'nowrap',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    animationDirection: 'normal',
    animationDuration: '10s',
    ['&.is-vertical']: {
      flexDirection: 'column',
      animationName: verticalScrollAnimation
    },
    ['&.is-horizontal']: {
      flexDirection: 'row',
      animationName: horizontalScrollAnimation
    },
    [`&:hover`]: {
      animationPlayState: 'paused !important'
    }
  }
}))

export const AutoScroll = ({
  classNames: externalClassNames,
  styles: externalStyles,
  orientation,
  speed = 50,
  children
}: PropsWithChildren<AutoScrollProps>) => {
  const { cx, styles } = useStyles()

  const [copyChildren, setCopyChildren] = useState(true)
  const [shouldScroll, setShouldScroll] = useState(true)
  const [duration, setDuration] = useState(0)

  const rootWrapperRef = useRef<HTMLDivElement>(null)
  const scrollWrapperRef = useRef<HTMLDivElement>(null)

  const { run: scrollUpdator } = useDebounceFn(
    () => {
      const rootWrapperDom = rootWrapperRef.current
      const scrollWrapperDom = scrollWrapperRef.current

      if (!(rootWrapperDom && scrollWrapperDom)) return

      const { width: rootWrapperWidth, height: rootWrapperHeight } =
        rootWrapperDom.getBoundingClientRect()
      const { width: scrollWrapperWidth, height: scrollWrapperHeight } =
        scrollWrapperDom.getBoundingClientRect()
      const rootWrapperSize =
        orientation === 'vertical' ? rootWrapperHeight : rootWrapperWidth
      const scrollWrapperSize =
        orientation === 'vertical' ? scrollWrapperHeight : scrollWrapperWidth
      const shouldScroll = scrollWrapperSize > rootWrapperSize
      setShouldScroll(shouldScroll)
      setCopyChildren(shouldScroll)
      setDuration(scrollWrapperSize / speed)
    },
    { wait: 200, leading: true }
  )

  useMount(scrollUpdator)

  useEffect(() => {
    if (rootWrapperRef.current && scrollWrapperRef.current) {
      const resizeObserver = new ResizeObserver(scrollUpdator)
      resizeObserver.observe(rootWrapperRef.current)
      resizeObserver.observe(scrollWrapperRef.current)
      return () => {
        if (!resizeObserver) return
        resizeObserver.disconnect()
      }
    }
  }, [scrollUpdator])

  return (
    <div
      ref={rootWrapperRef}
      className={cx(styles.rootWrapper, externalClassNames?.rootWrapper)}
      style={externalStyles?.rootWrapper}
    >
      <div
        ref={scrollWrapperRef}
        className={externalClassNames?.scrollWrapper}
        style={{
          width: orientation === 'vertical' ? '100%' : 'max-content',
          ...externalStyles?.scrollWrapper
        }}
      >
        <div
          className={cx(
            styles.scroller,
            `is-${orientation}`,
            externalClassNames?.scroller
          )}
          style={{
            animationPlayState: shouldScroll ? 'running' : 'paused',
            animationDuration: `${duration}s`,
            ...externalStyles?.scroller
          }}
        >
          {children}
          {copyChildren && children}
        </div>
      </div>
    </div>
  )
}
